import {
  IFloorWithResources,
  IResourceWithSchedules,
  IUserWithResource,
  useAuthenticated,
  useGetAmenities,
  useOfficeResources
} from '@gettactic/hooks';
import { IResource, IUserFull, Office, Tag } from '@gettactic/api';
import React from 'react';
import { convertTZ, parseApiDate } from '@/lib/utils';
import { isTacticStaff } from '@/lib/featureFlags';
import { addMinutes, format, isSameDay } from 'date-fns';
import { setStorageFloor } from '@gettactic/helpers/src/resources/setStorageFloor';
import { useRouter } from 'next/router';
import { useDefaultOfficeHours } from '@gettactic/hooks';
import { ICommonContextStates } from '@/contexts/CommonContext';
import useCommonContext from '@/hooks/useCommonContext';
import { StartEndTimeMode } from '@gettactic/helpers';
import { useDisclosure, UseDisclosureReturn } from '@gettactic/helpers';
import {
  useMapViewOverlaysPopper,
  UseMapViewOverlaysPopperReturn
} from '@webV2/map/MapView/useMapViewOverlaysPopper';
import { MapInteractiveZoneActionOptions } from '@gettactic/map-builder/src/types';
import { MapCanvasValue } from '@gettactic/map-builder/src/components/map/contexts/MapCanvasContext';
import { useReserve } from '@webV2/providers/ReserveProvider/context';
import {
  useEventHandlerManager,
  UseEventHandlerManagerReturn
} from '@gettactic/helpers/src/hooks/useEventHandlerManager';
import { InteractiveZonesInfo } from '@gettactic/helpers/src/map/types';

interface MapProviderEvents {
  onHighlightResource: (resource: IResource | null, userId?: string) => void;
  onFocusResource: (resource: IResource | null) => void;
  onLocateResource: (resource: IResource | null) => void;
  onHighlightUserResource: (resources: IResource[] | null) => void;
}

export interface MapPageOptions {
  schedulable?: IResourceWithSchedules[];
  floors?: IResource[];
  floorsWithResources?: IFloorWithResources[];
  usersAtOffice?: IUserWithResource[];
  resources?: IResource[];
  allResources?: IResource[];
  floor: IResource | null;
  currentOffice: Office;
  startDate: Date | null;
  endDate: Date | null;
  timeMode?: StartEndTimeMode;
  setTimeMode?: (timeMode: StartEndTimeMode) => void;
  isDataLoading?: boolean;
  selectedResource: IResource | null;
  isMapEditing: boolean;
  isEmbedded: boolean;
  isMapBuilderMap: boolean;
  highlightedResource: IResource | null;
  poppers: UseMapViewOverlaysPopperReturn;
  openResourceModal: boolean;
  editGroupId: string | null;
  mapBuilderData: MapCanvasValue | undefined;
  editingMapDisclosure: UseDisclosureReturn;
  resourceIdHovering: string | null;
  focusedResource: string | null;
  events: UseEventHandlerManagerReturn<MapProviderEvents>;
  amenities: Tag[];
  amenitiesFilter: Tag[];
  userFocusDisclosure: UseDisclosureReturn<IUserFull>;
  mapFiltersDisclosure: UseDisclosureReturn;
  zonesInfo: InteractiveZonesInfo;
  locateResourceId: string | null;
  liveStartDate: Date | null;
  liveEndDate: Date | null;
  mapChangesCount: number;
  forceMapBuilder: boolean;
}

export interface MapStateActions {
  setFloor: (newFloor: IResource | null) => void;
  changeEndTime: (date: Date | null) => void;
  changeStartDate: (date: Date | null) => void;
  setSelectedResource: React.Dispatch<React.SetStateAction<IResource | null>>;
  setHighlightedResource: React.Dispatch<
    React.SetStateAction<IResource | null>
  >;
  setOpenResourceModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditGroupId: React.Dispatch<React.SetStateAction<string | null>>;
  highlightResource: (
    resId: string | null,
    target: Element | MapInteractiveZoneActionOptions | null
  ) => void;
  setForceMapBuilder: React.Dispatch<React.SetStateAction<boolean>>;
  setResourceIdHovering: React.Dispatch<React.SetStateAction<string | null>>;
  setFocusedResource: React.Dispatch<React.SetStateAction<string | null>>;
  startReserve: (resource: IResource) => void;
  setResourcesIdFilter: React.Dispatch<React.SetStateAction<string[]>>;
  setStartDate: React.Dispatch<React.SetStateAction<Date | null>>;
  applyAmenitiesFilter: (ids: string[]) => void;
  selectResource: (
    resId: string,
    center?: boolean,
    callback?: () => void
  ) => void;
  setLocateResourceId: React.Dispatch<React.SetStateAction<string | null>>;
  setZonesInfo: React.Dispatch<React.SetStateAction<InteractiveZonesInfo>>;
  setMapChangesCount: React.Dispatch<React.SetStateAction<number>>;
}

export type MapPageState = MapPageOptions & MapStateActions;

export const MapPageContext = React.createContext<MapPageState | null>(null);

export interface EditingMapDisclosureData {
  mode: 'svg' | 'map-builder';
}

export interface MapProviderProps {
  isEmbedded?: boolean;
  officeId?: string;
  floorId?: string;
  startDate?: string | null;
}

export const MapProvider: React.FC<
  React.PropsWithChildren<MapProviderProps>
> = ({
  children,
  isEmbedded = false,
  officeId: controlledOfficeId,
  floorId: controlledFloorId,
  startDate: controlStartDate
}) => {
  // Let's manage some events "stateless" to prevent re-renders when not strictly necessary ( useful on huge maps )
  const events = useEventHandlerManager<MapProviderEvents>();
  const router = useRouter();
  const { openReserveModal } = useReserve();
  const {
    userContext: { authenticatedUser, currentOffice: contextSelectedOffice },
    setUserContext
  } = useAuthenticated();
  const { setCommon } = useCommonContext();
  const [locateResourceId, setLocateResourceId] = React.useState<string | null>(
    null
  );

  const [mapChangesCount, setMapChangesCount] = React.useState(0);

  const currentOffice =
    (controlledOfficeId
      ? authenticatedUser?.offices?.byId?.[controlledOfficeId]
      : null) || contextSelectedOffice;

  const amenitiesResponse = useGetAmenities();
  const amenities = amenitiesResponse?.data?.result?.tags || [];

  const today = convertTZ(new Date(), currentOffice.time_zone);

  const urlParamsReady = React.useRef<boolean>(false);

  const [_startDate, setStartDate] = React.useState<Date | null>(
    new Date(convertTZ(new Date(), currentOffice.time_zone).getTime())
  );

  const [_endDate, setEndDate] = React.useState<Date | null>(null);
  const [liveStartDate, setLiveStartDate] = React.useState<Date | null>(
    new Date()
  );
  const [liveEndDate, setLiveEndDate] = React.useState<Date | null>(null);

  const [resourcesIdFilter, setResourcesIdFilter] = React.useState<string[]>(
    []
  );
  const [filterQuery, setFilterQuery] = React.useState('');
  const [editGroupId, setEditGroupId] = React.useState<string | null>(null);
  const [openResourceModal, setOpenResourceModal] = React.useState(false);
  const [openSidePanel, setOpenSidePanel] = React.useState(false);
  const isTacticStaffAuthenticatedUser = !!isTacticStaff(authenticatedUser);

  const [timeMode, setTimeMode] = React.useState<StartEndTimeMode>('manual');

  const [resourceIdHovering, setResourceIdHovering] = React.useState<
    string | null
  >(null);

  const [selectedResource, setSelectedResource] =
    React.useState<IResource | null>(null);
  const [highlightedResource, setHighlightedResource] =
    React.useState<IResource | null>(null);
  const [focusedResource, setFocusedResource] = React.useState<string | null>(
    null
  );

  const startDate = timeMode === 'manual' ? _startDate : liveStartDate;
  const endDate = timeMode === 'manual' ? _endDate : liveEndDate;

  const mapFiltersDisclosure = useDisclosure();

  const editingMapDisclosure = useDisclosure<EditingMapDisclosureData>();
  const userFocusDisclosure = useDisclosure<IUserFull>();

  const isMapEditing = editingMapDisclosure.isOpen;

  const [amenitiesFilter, setAmenitiesFilter] = React.useState<Tag[]>([]);

  const applyAmenitiesFilter = (ids: string[]) => {
    if (!amenities || amenities.length === 0) {
      setAmenitiesFilter([]);
      return;
    }
    const filtered = amenities.filter((amenity) => ids.includes(amenity.id));
    setAmenitiesFilter(filtered);
  };

  const {
    schedulable,
    floors,
    floorsWithResources,
    usersAtOffice,
    resources,
    loading: isDataLoading,
    floor: _floor,
    setFloor: _setFloor,
    reload: reloadResources
  } = useOfficeResources({
    officeId:
      controlledOfficeId || (urlParamsReady.current ? currentOffice.id : null),
    startDate,
    endDate,
    filterQuery,
    entireOffice: isMapEditing || openSidePanel,
    getAllMaps: isTacticStaffAuthenticatedUser,
    coreApi: true,
    tags: amenitiesFilter.map((tag) => tag.id),
    floorId: controlledFloorId
  });

  const [floor, __setFloor] = React.useState<IResource | null>(null);

  React.useEffect(() => {
    if (!floor && _floor) {
      __setFloor(_floor);
    }
  }, [floors, floor, _floor]);

  const setFloor = (newFloor: IResource | null) => {
    __setFloor(newFloor);
    _setFloor(newFloor);
    setOpenResourceModal(false);
    setSelectedResource(null);
    setHighlightedResource(null);
    setFocusedResource(null);
  };

  React.useEffect(() => {
    if (timeMode !== 'live') return;
    let timeoutId: NodeJS.Timeout | undefined = undefined;
    let recurringTimeoutId: NodeJS.Timeout;

    function setLiveTick() {
      const now = new Date();
      setLiveStartDate(now); // 30 minutes ago
      setLiveEndDate(addMinutes(now, 1));
      reloadResources();
    }

    function startUpdates() {
      setLiveTick();

      // Schedule next update in 60s
      recurringTimeoutId = setTimeout(startUpdates, 60000);
    }

    const now = new Date();
    const msToNextMinutePlus5s = (60 - now.getSeconds()) * 1000;

    setLiveTick();
    timeoutId = setTimeout(() => {
      startUpdates();
    }, msToNextMinutePlus5s);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(recurringTimeoutId);
    };
  }, [timeMode]);

  const defaultOfficeHoursCalculator = useDefaultOfficeHours(currentOffice);

  const changeEndTime = React.useCallback(
    (date: Date | null) => {
      if (
        date === null ||
        startDate === null ||
        date.getTime() - startDate.getTime() <= 0
      ) {
        // avoid querying api with invalid values
        return;
      }
      setEndDate(date);
    },
    [startDate]
  );

  const changeStartDate = React.useCallback(
    (date: Date | null) => {
      if (date === null || endDate === null || startDate === null) {
        return;
      }
      const defaultHours = defaultOfficeHoursCalculator(date);
      let updateDefaultHours = false;

      // Check if the incoming date has a specific time set (not midnight)
      const hasSpecificTime =
        date.getHours() !== 0 ||
        date.getMinutes() !== 0 ||
        date.getSeconds() !== 0;

      if (!isSameDay(date, startDate) && defaultHours && !hasSpecificTime) {
        date.setHours(defaultHours.startHour, defaultHours.startMinutes, 0);
        updateDefaultHours = true;
      }

      const currentDiff = endDate.getTime() - startDate.getTime();
      setStartDate(date);
      setCommon((context: ICommonContextStates | null) => ({
        ...context,
        start: date
      }));

      if (updateDefaultHours && defaultHours) {
        endDate.setHours(defaultHours.endHour, defaultHours.endMinutes, 0);
      }

      if (isSameDay(endDate, date)) {
        return;
      }
      const maxTime = new Date(date);
      maxTime.setHours(23, 30, 0);
      const newEndDate = new Date(new Date(date).getTime() + currentDiff);
      if (updateDefaultHours && defaultHours) {
        newEndDate.setHours(defaultHours.endHour, defaultHours.endMinutes, 0);
      }
      setEndDate(isSameDay(date, newEndDate) ? newEndDate : maxTime);
    },
    [endDate, defaultOfficeHoursCalculator, startDate, setCommon]
  );

  React.useEffect(() => {
    if (!router.isReady) {
      return;
    }
    urlParamsReady.current = true;
    const [path, hash] = (router.asPath as string).split('#');
    const parsedHash = new URLSearchParams(hash);
    const resourceId = ''; //parsedHash.get('resource');
    const officeId = ''; // parsedHash.get('office');
    const floorId = controlledFloorId || ''; // parsedHash.get('floor');
    const date = controlStartDate || ''; // parsedHash.get('date');
    let officeExists: Office | undefined;
    let floorExists: IResource | undefined;
    if (date && startDate && format(startDate, 'yyyy-MM-dd') !== date) {
      const newDate = parseApiDate(date);
      const defaultHours = defaultOfficeHoursCalculator(newDate);
      if (defaultHours) {
        newDate.setHours(defaultHours?.startHour, defaultHours?.startMinutes);
      } else {
        newDate.setHours(9, 0);
      }
      changeStartDate(newDate);
      return;
    }
    if (!controlledOfficeId && officeId && currentOffice.id !== officeId) {
      officeExists = authenticatedUser.offices?.offices.find(
        (o) => o.id === officeId
      );
      if (officeExists) {
        setUserContext({ type: 'updateCurrentOffice', payload: officeId });
      }
      return;
    }

    if (!controlledFloorId && floorId && floorId !== floor?.id) {
      floorExists = floors?.find((x) => floorId === x.id);
      if (floorExists) {
        setFloor(floorExists ?? null);
        setStorageFloor(floorId);
      }
      return;
    }

    if (controlledFloorId) {
      floorExists = floors?.find((x) => floorId === x.id);
      if (floorExists) {
        setFloor(floorExists ?? null);
      }
      return;
    }
  }, [
    authenticatedUser.offices?.offices,
    changeStartDate,
    setUserContext,
    setFloor,
    router,
    schedulable,
    currentOffice,
    startDate,
    defaultOfficeHoursCalculator,
    floors,
    floor?.id,
    controlledFloorId,
    controlledOfficeId,
    controlStartDate
  ]);

  React.useEffect(() => {
    const defaultOfficeHours = defaultOfficeHoursCalculator(today);
    if (!currentOffice || !defaultOfficeHours) {
      return;
    }

    const defaultHours = defaultOfficeHours;
    today.setHours(defaultHours.startHour, defaultHours.startMinutes, 0);
    const currentDate = new Date(today.getTime());
    setCommon((context: ICommonContextStates | null) => ({
      ...context,
      start: currentDate
    }));
    setStartDate(currentDate);
    today.setHours(defaultHours.endHour, defaultHours.endMinutes, 0);
    setEndDate(new Date(today.getTime()));
  }, [currentOffice.time_zone, currentOffice.id, defaultOfficeHoursCalculator]);

  const [forceMapBuilder, setForceMapBuilder] = React.useState(false);
  const mapData = floor?.map_data ? JSON.parse(floor?.map_data) : undefined;
  const isMapBuilderMap = forceMapBuilder || (!floor?.map_url && !!mapData);

  const poppers = useMapViewOverlaysPopper(false);

  const highlightResource: MapStateActions['highlightResource'] =
    React.useCallback(
      (resId, target) => {
        if (!schedulable || !resId) {
          setHighlightedResource(null);
          return;
        }
        const res = schedulable?.find((x) => x.id === resId);
        if (!res) {
          poppers.setReferenceDetailsHighlightElement(null);
          setHighlightedResource(null);
          return;
        }
        if (isMapBuilderMap) {
          const targetOption = target as MapInteractiveZoneActionOptions;
          poppers.setHighlightResourceCoords({
            x: targetOption.x || 0,
            y: targetOption.y || 0,
            width: targetOption.width || 0,
            height: targetOption.height || 0
          });
        } else {
          poppers.setReferenceDetailsHighlightElement(target as Element);
        }
        setHighlightedResource(res);
      },
      [schedulable, isMapBuilderMap]
    );

  const startReserve = (resource: IResource) => {
    openReserveModal({
      start: startDate || undefined,
      time: startDate,
      resource: resource,
      duration: 0,
      floorId: floor?.id ?? null,
      resourceType: resource.type,
      office: currentOffice
    });
  };

  const filteredResources = React.useMemo(() => {
    if (!resources) return [];

    if (!resourcesIdFilter || resourcesIdFilter.length === 0) {
      return resources;
    }
    return resources.filter((resource) =>
      resourcesIdFilter.includes(resource.id)
    );
  }, [resources, resourcesIdFilter]);

  const allowEditingPointsAndUploads =
    isTacticStaffAuthenticatedUser ||
    authenticatedUser?.user?.is_being_impersonated;

  const selectResource = React.useCallback(
    (resId: string, center = false, callback?: () => void) => {
      if (!schedulable) {
        return;
      }
      const res = schedulable?.find((x) => x.id === resId);

      if (allowEditingPointsAndUploads) {
        setEditGroupId(resId);
      }
      const targetFloor = floorsWithResources?.find((f) =>
        f.resources.find((x) => x.id === resId)
      );

      const setAllMapResources = () => {
        //setOpenSidePanel(false);
        highlightResource(null, null);
        setOpenResourceModal(true);
        setSelectedResource(res || null);

        if (res) {
          if (center) {
          } else {
            poppers.setReferenceResDetailsElement(
              document.getElementById(`interactive-${resId}`)
            );
          }
        }
        callback?.();
      };
      if (targetFloor) {
        setFloor(targetFloor);
        setStorageFloor(targetFloor.id);
      }
      setTimeout(setAllMapResources, floor?.id !== targetFloor?.id ? 500 : 0);
    },
    [
      setFloor,
      floor,
      floorsWithResources,
      schedulable,
      allowEditingPointsAndUploads,
      highlightResource
    ]
  );

  const [zonesInfo, setZonesInfo] = React.useState<InteractiveZonesInfo>({
    mapInfo: null,
    zones: []
  });

  return (
    <MapPageContext.Provider
      value={{
        schedulable,
        floors,
        floorsWithResources,
        usersAtOffice,
        resources: filteredResources,
        allResources: resources,
        floor,
        setFloor: (newFloor) => {
          setFloor(newFloor);
          setStorageFloor(newFloor?.id);
          setOpenResourceModal(false);
          setSelectedResource(null);
          setHighlightedResource(null);
          setFocusedResource(null);
        },
        startDate,
        endDate,
        currentOffice,
        changeEndTime,
        changeStartDate,
        timeMode,
        setTimeMode,
        isDataLoading,
        selectedResource,
        setSelectedResource,
        isMapEditing,
        highlightedResource,
        setHighlightedResource,
        isEmbedded,
        isMapBuilderMap,
        poppers,
        setEditGroupId,
        editGroupId,
        openResourceModal,
        setOpenResourceModal,
        highlightResource,
        mapBuilderData: mapData,
        setForceMapBuilder,
        editingMapDisclosure,
        resourceIdHovering,
        setResourceIdHovering,
        focusedResource,
        setFocusedResource,
        startReserve,
        setResourcesIdFilter,
        setStartDate,
        events,
        amenities,
        amenitiesFilter,
        applyAmenitiesFilter,
        userFocusDisclosure,
        selectResource,
        mapFiltersDisclosure,
        zonesInfo,
        setZonesInfo,
        locateResourceId,
        setLocateResourceId,
        liveStartDate,
        liveEndDate,
        mapChangesCount,
        setMapChangesCount,
        forceMapBuilder
      }}
    >
      {children}
    </MapPageContext.Provider>
  );
};

export const useMap = () => {
  const context = React.useContext(MapPageContext);
  if (!context) {
    throw new Error('useMap must be used within a MapPageProvider');
  }
  return context;
};
